"use client";
import React, { useState, useMemo } from "react";
import { CalendarDays, ArrowRightCircle, Info, AlertTriangle, Ban, Infinity as InfinityIcon } from "lucide-react";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MOCK_EMPLOYEES = [
  { id: "1", name: "Aarav Sharma" },
  { id: "2", name: "Priya Nair" },
  { id: "3", name: "Rohit Verma" },
];

// ---------------------------------------------------------------------------
// Company-level default, set on the OrgLeavePolicySettings page. Every
// leave type below inherits this unless it has its own override — that's
// why Casual/Earned have `null` on their carry-forward fields: they follow
// whatever the company sets here. Sick Leave shows what an override looks
// like (it always stays "unlimited" regardless of the company default).
//
// In the real app, fetch this from the same OrgSettingsContext that backs
// OrgLeavePolicySettings.jsx instead of hardcoding it — that's the single
// source of truth. Bump expiry_months from 6 to 12 here and every
// non-overridden leave type in the ledger below switches immediately.
// ---------------------------------------------------------------------------
const MOCK_ORG_POLICY = {
  policy: "expiring",
  max_carry_forward: 4,
  expiry_months: 6,
};

// carry_forward_policy / max_carry_forward / carry_forward_expiry_months:
//   `null` = inherit the company default above. A real value = this leave
//   type overrides the company standard.
const MOCK_LEAVE_TYPES = [
  {
    id: "1",
    name: "Casual Leave",
    monthly_limit: 1.5,
    carry_forward_policy: null,
    max_carry_forward: null,
    carry_forward_expiry_months: null,
  },
  {
    id: "2",
    name: "Sick Leave",
    monthly_limit: 1,
    carry_forward_policy: "unlimited",
    max_carry_forward: null,
    carry_forward_expiry_months: null,
  },
  {
    id: "3",
    name: "Earned Leave",
    monthly_limit: 2,
    carry_forward_policy: null,
    max_carry_forward: null,
    carry_forward_expiry_months: null,
  },
];

// Same resolver exported from OrgLeavePolicySettings.jsx — duplicated inline
// here so this file works standalone; in your app, import the one copy.
function resolveEffectivePolicy(leaveType, orgPolicy) {
  const policy = leaveType.carry_forward_policy ?? orgPolicy.policy;
  return {
    policy,
    max_carry_forward: leaveType.max_carry_forward ?? orgPolicy.max_carry_forward ?? null,
    expiry_months:
      policy === "expiring" ? leaveType.carry_forward_expiry_months ?? orgPolicy.expiry_months : null,
  };
}

const MOCK_USAGE = {
  "1-1": [0.5, 0, 1, 0, 0.5, 0, 1.5, 0, 0, 1, 0, 0.5],
  "1-2": [0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0],
  "1-3": [0, 0, 2, 0, 0, 0, 0, 2, 0, 0, 0, 0],
  "2-1": [1.5, 0, 0, 1, 0, 0, 0.5, 0, 1, 0, 0, 0],
  "2-2": [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 1],
  "2-3": [0, 0, 0, 0, 2, 0, 0, 0, 2, 0, 0, 0],
  "3-1": [0, 0.5, 0, 1, 0, 0, 0, 0.5, 0, 0, 1, 0],
  "3-2": [1, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0],
  "3-3": [0, 0, 0, 2, 0, 0, 0, 0, 0, 2, 0, 0],
};

// ---------------------------------------------------------------------------
// Builds a month-by-month ledger honoring the leave type's carry-forward
// policy. Carried-forward days are tracked as "batches" (amount + the month
// they expire in), consumed oldest-first (FIFO), so a 6-month expiry policy
// forfeits exactly the days that have sat unused for 6 months — not just a
// running total.
// ---------------------------------------------------------------------------
function buildLedger(usedByMonth, monthlyLimit, policy, maxCarry, expiryMonths) {
  let batches = []; // { amount, expiresAt: monthIndex (inclusive), createdAt }
  const rows = [];

  for (let idx = 0; idx < 12; idx++) {
    // 1. Expire any batch whose window has already closed before this month
    let forfeited = 0;
    if (policy === "expiring") {
      const stillValid = [];
      batches.forEach((b) => {
        if (b.expiresAt < idx) forfeited += b.amount;
        else stillValid.push(b);
      });
      batches = stillValid;
    }

    const opening = round2(batches.reduce((s, b) => s + b.amount, 0));
    const totalAvailable = round2(opening + monthlyLimit);
    const used = Math.min(usedByMonth[idx] || 0, totalAvailable);

    // 2. Consume FIFO — oldest carried-forward batches before this month's fresh allocation
    let remaining = used;
    const survivors = [];
    for (const b of batches) {
      if (remaining <= 0) {
        survivors.push(b);
        continue;
      }
      if (remaining >= b.amount) {
        remaining -= b.amount;
      } else {
        survivors.push({ ...b, amount: round2(b.amount - remaining) });
        remaining = 0;
      }
    }
    batches = survivors;
    const usedFromMonthly = Math.min(remaining, monthlyLimit);
    const monthlyLeftover = round2(monthlyLimit - usedFromMonthly);

    // 3. Fold this month's leftover into the pool (unless policy forbids carry forward)
    if (policy !== "none" && monthlyLeftover > 0) {
      batches.push({
        amount: monthlyLeftover,
        createdAt: idx,
        expiresAt: policy === "expiring" ? idx + expiryMonths : Infinity,
      });
    }

    // 4. Enforce the cap, trimming the newest batch(es) first so days closer
    //    to expiring are preserved over fresher ones
    if (policy !== "none" && maxCarry != null) {
      let total = batches.reduce((s, b) => s + b.amount, 0);
      let excess = round2(total - maxCarry);
      for (let i = batches.length - 1; i >= 0 && excess > 0; i--) {
        const cut = Math.min(batches[i].amount, excess);
        batches[i].amount = round2(batches[i].amount - cut);
        excess = round2(excess - cut);
      }
      batches = batches.filter((b) => b.amount > 0);
    }

    const carriedForward = round2(batches.reduce((s, b) => s + b.amount, 0));
    const nearestExpiry =
      policy === "expiring" && batches.length ? Math.min(...batches.map((b) => b.expiresAt)) : null;

    rows.push({
      month: MONTHS[idx],
      monthIndex: idx,
      opening,
      monthlyLimit,
      totalAvailable,
      used: round2(used),
      remaining: round2(totalAvailable - used),
      carriedForward,
      forfeited: round2(forfeited),
      nearestExpiry,
    });
  }
  return rows;
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function expiryLabel(monthIndex) {
  if (monthIndex == null || !isFinite(monthIndex)) return null;
  const wrapped = ((monthIndex % 12) + 12) % 12;
  const yearOffset = Math.floor(monthIndex / 12);
  return `${MONTHS[wrapped]}${yearOffset > 0 ? ` (yr+${yearOffset})` : ""}`;
}

const POLICY_META = {
  none: { label: "No carry forward", icon: Ban, className: "bg-gray-100 text-gray-500" },
  unlimited: { label: "Unlimited carry forward", icon: InfinityIcon, className: "bg-blue-100 text-blue-600" },
  expiring: { label: "Expires after N months", icon: ArrowRightCircle, className: "bg-amber-100 text-amber-700" },
};

export default function LeaveCarryForward() {
  const [selectedEmployee, setSelectedEmployee] = useState(MOCK_EMPLOYEES[0].id);
  const [selectedTypeId, setSelectedTypeId] = useState(MOCK_LEAVE_TYPES[0].id);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const selectedType = MOCK_LEAVE_TYPES.find((t) => t.id === selectedTypeId);
  const monthlyLimit = selectedType?.monthly_limit ?? 0;

  // Resolve against the company-level default — this is the piece that
  // makes "set it to 6 here, 12 there" work per company: change
  // MOCK_ORG_POLICY.expiry_months and every leave type without its own
  // override (i.e. carry_forward_expiry_months === null) follows it.
  const effective = selectedType
    ? resolveEffectivePolicy(selectedType, MOCK_ORG_POLICY)
    : { policy: "none", max_carry_forward: null, expiry_months: null };
  const policy = effective.policy;
  const maxCarryForward = effective.max_carry_forward;
  const expiryMonths = effective.expiry_months;
  const isUsingCompanyDefault =
    selectedType?.carry_forward_policy == null && selectedType?.carry_forward_expiry_months == null;

  const usedByMonth = useMemo(() => {
    const key = `${selectedEmployee}-${selectedTypeId}`;
    return MOCK_USAGE[key] || Array(12).fill(0);
  }, [selectedEmployee, selectedTypeId]);

  const ledger = useMemo(
    () => buildLedger(usedByMonth, monthlyLimit, policy, maxCarryForward, expiryMonths),
    [usedByMonth, monthlyLimit, policy, maxCarryForward, expiryMonths]
  );

  const currentMonthIndex = new Date().getMonth();
  const currentRow =
    Number(selectedYear) === new Date().getFullYear()
      ? ledger[currentMonthIndex]
      : ledger[ledger.length - 1];

  const totalForfeitedThisYear = useMemo(
    () => round2(ledger.reduce((s, r) => s + r.forfeited, 0)),
    [ledger]
  );

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return [now - 1, now, now + 1];
  }, []);

  const PolicyIcon = POLICY_META[policy]?.icon ?? Ban;

  return (
    <div className="bg-sky-50">
      <div className="px-4 sm:px-6 md:px-9 py-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
          Leave Balance &amp; Carry Forward
        </h1>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6 bg-white rounded-xl p-3 shadow-sm">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500">Employee</label>
            <select
              className="w-full border rounded-md px-3 py-2 mt-1 text-sm"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              {MOCK_EMPLOYEES.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500">Leave Type</label>
            <select
              className="w-full border rounded-md px-3 py-2 mt-1 text-sm"
              value={selectedTypeId}
              onChange={(e) => setSelectedTypeId(e.target.value)}
            >
              {MOCK_LEAVE_TYPES.map((lt) => (
                <option key={lt.id} value={lt.id}>
                  {lt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500">Year</label>
            <select
              className="w-full border rounded-md px-3 py-2 mt-1 text-sm"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* Policy pill — reflects whatever was configured on the Leave Types page */}
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500">Policy</label>
            <div
              className={`mt-1 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium w-full ${POLICY_META[policy]?.className}`}
            >
              <PolicyIcon className="h-4 w-4" />
              {POLICY_META[policy]?.label}
              {policy === "expiring" && expiryMonths ? ` (${expiryMonths}mo)` : ""}
              {isUsingCompanyDefault && (
                <span className="text-[10px] uppercase tracking-wide opacity-70 border border-current rounded px-1.5 py-0.5 ml-auto">
                  company default
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <StatCard label="Monthly Limit" value={monthlyLimit} accent="#2563eb" />
          <StatCard label="Used This Month" value={currentRow?.used ?? 0} accent="#d12465" />
          <StatCard label="Remaining This Month" value={currentRow?.remaining ?? 0} accent="#0ea5e9" />
          <StatCard
            label="Carried Forward to Next Month"
            value={currentRow?.carriedForward ?? 0}
            accent="#16a34a"
            icon={<ArrowRightCircle className="h-4 w-4" />}
          />
          <StatCard
            label="Forfeited This Year"
            value={totalForfeitedThisYear}
            accent="#dc2626"
            icon={<AlertTriangle className="h-4 w-4" />}
          />
        </div>

        {policy === "none" && (
          <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 text-yellow-700 text-sm rounded-lg px-3 py-2 mb-6">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Carry forward is disabled for <strong>{selectedType?.name}</strong>. Unused days lapse
              at the end of every month.
            </span>
          </div>
        )}

        {policy === "expiring" && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-3 py-2 mb-6">
            <ArrowRightCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              <strong>{selectedType?.name}</strong> carries forward but must be used within{" "}
              <strong>{expiryMonths} months</strong>
              {maxCarryForward != null ? ` (capped at ${maxCarryForward} days)` : ""}. Days that age
              past that window are forfeited automatically — see the "Forfeited" column below.
            </span>
          </div>
        )}

        {/* Monthly ledger table */}
        <div className="bg-white shadow rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3 text-gray-700">
            <CalendarDays className="h-4 w-4" />
            <h2 className="font-semibold">
              Monthly Ledger — {selectedType?.name} ({selectedYear})
            </h2>
          </div>

          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-[880px] sm:min-w-full border-collapse text-sm">
              <thead className="bg-sky-50">
                <tr>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">Month</th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">Opening Balance</th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">Monthly Limit</th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">Total Available</th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">Used</th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">Remaining</th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">Carried Forward →</th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">Forfeited</th>
                  {policy === "expiring" && (
                    <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                      Next Expiry
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {ledger.map((row, idx) => (
                  <tr
                    key={row.month}
                    className={`border-b hover:bg-gray-50 ${
                      idx === currentMonthIndex && Number(selectedYear) === new Date().getFullYear()
                        ? "bg-sky-50/70"
                        : idx % 2 === 1
                        ? "bg-[#f7f6fe]"
                        : "bg-white"
                    }`}
                  >
                    <td className="py-3 px-2 sm:px-4 text-gray-700 whitespace-nowrap">{row.month}</td>
                    <td className="py-3 px-2 sm:px-4 whitespace-nowrap">{row.opening}</td>
                    <td className="py-3 px-2 sm:px-4 whitespace-nowrap">{row.monthlyLimit}</td>
                    <td className="py-3 px-2 sm:px-4 whitespace-nowrap">{row.totalAvailable}</td>
                    <td className="py-3 px-2 sm:px-4 whitespace-nowrap">{row.used}</td>
                    <td className="py-3 px-2 sm:px-4 whitespace-nowrap">{row.remaining}</td>
                    <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                      {row.carriedForward > 0 ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-600">
                          <ArrowRightCircle className="h-3 w-3" /> {row.carriedForward}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                      {row.forfeited > 0 ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                          <AlertTriangle className="h-3 w-3" /> {row.forfeited}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    {policy === "expiring" && (
                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap text-gray-600">
                        {expiryLabel(row.nearestExpiry) || <span className="text-gray-400">—</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, icon }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <div className="flex items-center gap-1">
        {icon && <span style={{ color: accent }}>{icon}</span>}
        <span className="text-2xl font-bold" style={{ color: accent }}>
          {value}
        </span>
        <span className="text-xs text-gray-400 mb-0.5">days</span>
      </div>
    </div>
  );
}